import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Table from '../../Components/Table'
import SessionStore from '../../State/SessionStore'
import RenderOnQueries from '../Layout/RenderOnQueries'
import ExpenseReportStore from './ExpenseReportStore'
import LoadingSpinner from '../../Components/LoadingSpinner'
import _ from 'lodash'
import { queryClient } from '../../App'
import UpdateReportModal from '../../Components/UpdateReportModal'
import { format } from 'date-fns'
import { useMediaQuery } from '@react-hook/media-query'

let renders = 0

export default observer(() => {
    const report = ExpenseReportStore.report
    if (!report) return null
    return (
        <RenderOnQueries
            queryIds={[
                {
                    id: report.queryKey,
                    path:
                        process.env.REACT_APP_NODE_SERVER_URL +
                        '/expense-report',
                    staleTime: 0,
                    cacheTime: 0,
                    method: 'POST',
                    data: {
                        organisationId: SessionStore.organisationId,
                        userId: SessionStore.user?.id,
                        columns: report.columns.filter(
                            (c) =>
                                !report.columnOptionsById[c]?.permissions ||
                                report.columnOptionsById[c].permissions()
                        ),
                        filters: report.filters.filter(Boolean),
                        groupBy: report.groupBy,
                        sortBy: report.sortBy,
                        dateRange: report.dateRange.map((d) =>
                            isFinite(d) ? format(d, 'yyyy-MM-dd') : null
                        ),
                    },
                },
            ]}
            loading={<LoadingSpinner />}
        >
            <ExpenseTable key={report.queryKey} />
        </RenderOnQueries>
    )
})

const ExpenseTable = observer(({ level = 0, parent = null }) => {
    const store = ExpenseReportStore
    const report = store.report
    const queryData = queryClient.getQueryData([report.queryKey])?.data
    useEffect(() => {
        store.init(queryData)
    }, [report.queryKey, queryData])
    if (store.batchSaving) return <LoadingSpinner />
    return (
        <>
            {!level ? <UpdateReportModal report={report} /> : null}
            <ExpenseTableTable
                level={level}
                parent={parent}
                key={report.queryKey}
            />
        </>
    )
})

const ExpenseTableTable = observer(({ level = 0, parent = null }) => {
    const isPrinting = useMediaQuery('print')
    const store = ExpenseReportStore
    const { report, groupBy, sortBy } = store
    const bgColor = 256 - level * 5
    const bgShadow = bgColor - 10
    const tableColumns = useMemo(
        () => report?.tableColumns.filter((c) => !store.groupBy.includes(c.id)),
        [report, store.groupBy]
    )

    const getChildComponent = useCallback(
        (r) => (
            <ExpenseTableTable
                parent={r}
                level={level + 1}
                key={`${report.queryKey}-${level}-${r.key}`}
            />
        ),
        [level, store.groupBy, report]
    )
    return (
        <Table
            expandAll={store.expandAll && store.expandToLevel > level}
            key={`${report.queryKey}-${level}-${parent?.key}`}
            tableStore={!level ? store.tableStore : null}
            columns={[
                {
                    value: (row) => {
                        return store.isRowSelected(row)
                    },
                    type: 'boolean',
                    width: 3,
                    id: 'checked',
                    label: '',
                    editable: (r) => true,
                    print: false,
                    onChange: (r, stores) => (v) => {
                        if (_.isEmpty(r)) {
                            if (v) {
                                store.selectAllRows()
                            } else {
                                store.deselectAllRows()
                            }
                            return
                        } else {
                            if (v) {
                                store.selectRow(r)
                            } else {
                                store.deselectRow(r)
                            }
                        }
                    },
                },
                {
                    value: (row) => row.label,
                    ...report?.columnOptionsById[groupBy[level]],
                    width: level < groupBy.length && !isPrinting ? 30 : 33,
                    id: 'label',
                    label: 'Label',
                    style: () => ({
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        paddingLeft: 1 + level * 1 + 'rem',
                        fontWeight: 800 - level * 100,
                    }),
                },
                ...tableColumns,
            ]}
            rows={parent?.children || store.queryData || []}
            sortBy={
                !level ? [...sortBy, ['date', 'desc']] : store.tableStore.sortBy
            }
            showTotals={!level}
            showHeader={!level}
            getChildComponent={
                level < store.groupBy.length ? getChildComponent : null
            }
            style={
                level
                    ? {
                          // borderBottom: 'none',
                          boxShadow: 'none',
                          background: `linear-gradient(180deg, rgb(${bgShadow},${bgShadow},${bgShadow}) 1px, rgb(${bgColor},${bgColor},${bgColor}) 8px)`,
                          fontSize: '1.2rem',
                      }
                    : null
            }
        />
    )
})
